import React, { useState, useEffect } from 'react';
import {
    Dialog,
    Box,
    TextField,
    Typography,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    FormHelperText
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import APIClient from '../utils/axios.util';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

const validationSchema = Yup.object().shape({
    region: Yup.string().required('Country/Region is required'),
    timezone: Yup.string().required('Timezone is required'),
    datetime: Yup.date()
        .required('Date and time is required')
        .min(new Date(Date.now() + 4 * 60 * 60 * 1000), 'Please schedule at least 4 hours in advance')
        .typeError('Please enter a valid date and time')
});

const ScheduleDemoForm = ({
    open,
    onClose,
    userData = {},
    setAlertContent,
    setAlertType,
    setAlert
}) => {
    const [countries, setCountries] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [minDatetime, setMinDatetime] = useState('');

    const initialValues = {
        region: '',
        timezone: '',
        datetime: ''
    };

    // fetch timezones
    function getTimeZones(country) {
        const UserDetails = JSON.parse(localStorage.getItem("UserDetails"));
        const token = UserDetails?.access_token || "";
        APIClient.get(`/demos/timezones/${country}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => {
                setTimezones(res.data.timezones || []);
            })
            .catch(() => {
                setTimezones([]);
            });
    }

    // Fetch countries on load
    useEffect(() => {
        const UserDetails = JSON.parse(localStorage.getItem("UserDetails"));
        const token = UserDetails?.access_token || "";
        APIClient.get('demos/countries', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => {
                setCountries(res.data.countries || []);
            })
            .catch(() => {
                setCountries([]);
            });
    }, []);

    // Compute minimum datetime for selected timezone
    const getMinDatetimeForTimezone = (timezone) => {
        const fourHoursLater = new Date(Date.now() + 4 * 60 * 60 * 1000);
        if (!timezone) return fourHoursLater.toISOString().slice(0, 16);

        try {
            const tzDate = new Date(fourHoursLater.toLocaleString('en-US', { timeZone: timezone }));
            const year = tzDate.getFullYear();
            const month = String(tzDate.getMonth() + 1).padStart(2, '0');
            const day = String(tzDate.getDate()).padStart(2, '0');
            const hours = String(tzDate.getHours()).padStart(2, '0');
            const minutes = String(tzDate.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
            return fourHoursLater.toISOString().slice(0, 16);
        }
    };

    const handleSubmit = (values, { setSubmitting }) => {
        setError('');
        const UserDetails = JSON.parse(localStorage.getItem("UserDetails"));
        const token = UserDetails?.access_token || "";
        const payload = {
            userId: UserDetails?.user?.id,
            companyEmail: UserDetails?.user?.companyEmail,
            country: values.region,
            timezone: values.timezone,
            scheduledDatetime: values.datetime
        };

        APIClient.post('demos/schedule', payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then(() => {
                // window.gtag('event', 'demo_scheduled', { demo_type: 'product_demo' });
                setSuccess(true);
                setAlertContent(`Demo scheduled successfully!`);
                setAlertType('success');
                setAlert(true);

                setTimeout(() => {
                    setSuccess(false);
                    onClose();
                }, 1000);
            })
            .catch((error) => {
                setError('Failed to schedule demo. Please try again.');
            })
            .finally(() => setSubmitting(false));
    };

    const handleClose = () => {
        setError('');
        setSuccess(false);
        setTimezones([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
            <Box sx={{ p: 4, overflowY: 'auto' }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Schedule a Personal Demo
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Demo scheduled successfully!
                    </Alert>
                )}

                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                        <Form>
                            {/* COUNTRY DROPDOWN */}
                            <FormControl fullWidth sx={{ mb: 3 }} error={touched.region && Boolean(errors.region)}>
                                <InputLabel>Country/Region *</InputLabel>
                                <Select
                                    value={values.region}
                                    label="Country/Region *"
                                    onChange={(e) => {
                                        const selected = e.target.value;
                                        setFieldValue('region', selected);
                                        setFieldValue('timezone', '');
                                        setFieldValue('datetime', '');
                                        getTimeZones(selected);
                                    }}
                                >
                                    <MenuItem value="">Select your region</MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.region && errors.region && <FormHelperText>{errors.region}</FormHelperText>}
                            </FormControl>

                            {/* TIMEZONE DROPDOWN */}
                            <FormControl
                                fullWidth
                                sx={{ mb: 3 }}
                                disabled={!values.region}
                                error={touched.timezone && Boolean(errors.timezone)}
                            >
                                <InputLabel>Timezone *</InputLabel>
                                <Select
                                    value={values.timezone}
                                    label="Timezone *"
                                    onChange={(e) => {
                                        setFieldValue('timezone', e.target.value);
                                        const newMin = getMinDatetimeForTimezone(e.target.value);
                                        setMinDatetime(newMin);
                                        setFieldValue('datetime', '');
                                    }}
                                >
                                    <MenuItem value="">Select timezone</MenuItem>
                                    {timezones.map((tz) => (
                                        <MenuItem key={tz} value={tz}>
                                            {tz}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.timezone && errors.timezone && (
                                    <FormHelperText>{errors.timezone}</FormHelperText>
                                )}
                            </FormControl>

                            {/* DATE-TIME PICKER */}
                            <FormControl
                                fullWidth
                                sx={{ mb: 3 }}
                                error={touched.datetime && Boolean(errors.datetime)}
                                disabled={!values.timezone}
                            >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        label="Preferred Date & Time"
                                        value={values.datetime ? dayjs(values.datetime) : null}
                                        minDateTime={minDatetime ? dayjs(minDatetime) : dayjs().add(4, 'hour')}
                                        onChange={(newValue) => {
                                            // const isoString = newValue ? newValue.toISOString() : '';
                                            const localString = newValue ? newValue.format("YYYY-MM-DDTHH:mm:ss") : "";
                                            setFieldValue('datetime', localString);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={touched.datetime && Boolean(errors.datetime)}
                                                helperText={touched.datetime && errors.datetime ? errors.datetime : 'Select a time in the selected timezone'}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </FormControl>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button variant="outlined" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" disabled={isSubmitting}>
                                    Schedule Demo
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Dialog>
    );
};

export default ScheduleDemoForm;
